import * as Sentry from '@sentry/react';
import { environmentManager, secretManager } from 'services/api/config';

const { sentryDsn, releaseTag } = secretManager;

const sentryEnvironment = environmentManager.isProductionEnv
  ? 'production'
  : 'development';

type CaptureExceptionParams = Parameters<(typeof Sentry)['captureException']>;
type CaptureMessageParams = Parameters<(typeof Sentry)['captureMessage']>;

export class AppErrorManager {
  private static instance: AppErrorManager;

  private constructor() {
    Sentry.init({
      dsn: sentryDsn,
      environment: sentryEnvironment,
      release: releaseTag,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      normalizeDepth: 6,
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 1.0,
      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['localhost', /^\//],
    });
  }

  static sendError = (...args: CaptureExceptionParams) => {
    Sentry.captureException(...args);
  };

  static sendMessage = (...args: CaptureMessageParams) => {
    Sentry.captureMessage(...args);
  };

  static initialize() {
    if (!AppErrorManager.instance) {
      AppErrorManager.instance = new AppErrorManager();
    }

    return AppErrorManager.instance;
  }
}
